// src/components/DownloadModal.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TbDownload, TbCrown } from 'react-icons/tb';

const DownloadModal = ({ isOpen, closeModal, downloadsRemaining, onDownload }) => {
  const navigate = useNavigate();
  
  const handleUpgrade = () => {
    closeModal();
    navigate('/plans', { 
      state: { 
        message: "You've used all your free downloads. Upgrade to download unlimited documents." 
      } 
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {downloadsRemaining > 0 ? (
                  <>
                    <Dialog.Title as="h3" className="text-xl font-slimbold leading-6 text-gray-900 flex items-center mb-6">
                      <TbDownload className="mr-2 text-blue-500" />
                      Free Downloads
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-gray-600 mb-2">
                        You have <span className="font-bold text-blue-600">{downloadsRemaining} free {downloadsRemaining === 1 ? 'download' : 'downloads'}</span> remaining.
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        New users get 3 free downloads. Upgrade to our premium plan for unlimited downloads.
                      </p>
                      <div className="mt-6 flex space-x-3 justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                          onClick={onDownload}
                        >
                          Download Now
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Dialog.Title as="h3" className="text-xl font-slimbold leading-6 text-gray-900 flex items-center mb-6">
                      <TbCrown className="mr-2 text-amber-500" />
                      Upgrade to Premium
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-gray-600 mb-2">
                        You've used all your free downloads.
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Upgrade to our premium plan to get unlimited downloads and access to all features.
                      </p>
                      <div className="p-4 bg-blue-50 rounded-lg my-4">
                        <h4 className="font-medium text-blue-800 mb-2">Premium Benefits:</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>✓ Unlimited resume & cover letter downloads</li>
                          <li>✓ Access to all premium templates</li>
                          <li>✓ AI-powered optimization tools</li>
                          <li>✓ Advanced customization options</li>
                        </ul>
                      </div>
                      <div className="mt-6 flex space-x-3 justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                          onClick={closeModal}
                        >
                          Maybe Later
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 focus:outline-none"
                          onClick={handleUpgrade}
                        >
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;